import classNames from 'classnames'
import { useMemo } from 'react'
import type {
  SegmentationCardData,
  SegmentationCarouselProps,
  SegmentationCarouselTheme,
} from 'src/types/segmentationCarousel'

import SegmentationCard from './SegmentationCard'

const MAX_ITEMS = 12

const isValidCard = (card?: SegmentationCardData) =>
  [card?.textPrimary, card?.textSecondary, card?.textTertiary].some((text) =>
    text?.trim()
  )

export default function SegmentationCarousel({
  theme = 'light',
  title,
  subtitle,
  enableScroll = false,
  cards = [],
}: SegmentationCarouselProps) {
  const normalizedTheme: SegmentationCarouselTheme =
    theme === 'dark' ? 'dark' : 'light'

  const validCards = useMemo(
    () => cards.filter(isValidCard).slice(0, MAX_ITEMS),
    [cards]
  )

  const hasTitle = Boolean(title?.trim())
  const hasSubtitle = Boolean(subtitle?.trim())

  if (!validCards.length) {
    return null
  }

  return (
    <section
      data-testid="segmentation-carousel-2-0"
      className={classNames(
        'w-full max-h-[486px] md:max-h-[355px]',
        normalizedTheme === 'dark'
          ? 'bg-darkThemeComponents border-y border-white/[12%]'
          : 'bg-white'
      )}
    >
      <div className="mx-auto w-full max-w-[1440px] px-4 pt-4 pb-[30px] md:px-0">
        {(hasTitle || hasSubtitle) && (
          <header className="mb-6 flex flex-col gap-1">
            {hasTitle && (
              <h2
                data-testid="segmentation-carousel-title"
                className={classNames(
                  'text-2xl font-bold leading-tight md:text-[42px]',
                  normalizedTheme === 'dark' ? 'text-white' : 'text-fullDark'
                )}
              >
                {title}
              </h2>
            )}
            {hasSubtitle && (
              <p
                className={classNames(
                  'text-sm md:text-base',
                  normalizedTheme === 'dark'
                    ? 'text-white/70'
                    : 'text-graySubtitle'
                )}
              >
                {subtitle}
              </p>
            )}
          </header>
        )}

        <ul
          className={classNames(
            'grid list-none grid-cols-2 gap-3 md:flex md:flex-nowrap md:gap-5',
            enableScroll
              ? 'md:overflow-x-auto md:scroll-smooth'
              : 'md:justify-start'
          )}
        >
          {validCards.map((card, index) => (
            <li
              key={`${card.textPrimary ?? ''}-${index}`}
              className={classNames(
                'flex min-w-0',
                enableScroll
                  ? 'md:w-[340px] md:shrink-0'
                  : 'md:max-w-[340px] md:flex-1'
              )}
            >
              <SegmentationCard card={card} theme={normalizedTheme} />
            </li>
          ))}
        </ul>
      </div>
    </section>
  )
}
